// Generic recovery prompt cho các bước có thể recover trong init flow.
// Dùng pattern này thay vì throw thẳng để user luôn có choice (retry/skip/abort).
//
// 3 mode tùy task:
//   - retry-only:  task không thể skip (vd login fail) → "Try again" / "Abort"
//   - retry-skip:  task có thể bỏ qua (vd workspace remote fail) → "Retry" / "Skip & continue" / "Abort"
//   - manual-fix:  task cần user tự fix tay (vd workspace path conflict) → "Provide value" / "Abort"
//
// Loop retry — caller gọi trong while loop, user chỉ exit khi pick abort hoặc success.
import { getActiveFlag, isNonInteractive, MissingFlagError } from "./non-interactive-context.js";
import { inputOrFlag, selectOrFlag } from "./prompt-or-flag.js";
import { log } from "./terminal-logger.js";

// User chọn abort recovery — caller catch để exit 0 graceful.
export class UserAbortedRecoveryError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UserAbortedRecoveryError";
  }
}

export type RecoveryAction = "retry" | "skip" | "abort";

interface PromptRetryOrSkipArgs {
  // Tiêu đề task fail (vd "Cài đặt gh CLI").
  taskName: string;
  // Lý do fail (extracted từ error message hoặc custom).
  reason: string;
  // Có cho phép skip task này không? false = chỉ Retry / Abort.
  allowSkip?: boolean;
  // Custom hint hiển thị trước menu (vd "Check mạng, VPN").
  hint?: string;
}

// Show menu Retry/Skip/Abort. Trả về action user chọn.
// Caller responsibility: implement retry logic + handle skip semantic theo task.
const ON_FAILURE_FLAG_SPEC = {
  flag: "--on-failure",
  command: "add repo",
  hint: "--on-failure abort|retry|skip",
};
const REMOTE_URL_FLAG_SPEC = {
  flag: "--remote-url",
  command: "add repo",
  hint: "--remote-url <url>",
};

export async function promptRetryOrSkip(args: PromptRetryOrSkipArgs): Promise<RecoveryAction> {
  log.warn(`${args.taskName} thất bại: ${args.reason}`);
  if (args.hint) log.info(args.hint);

  const choices: Array<{ name: string; value: RecoveryAction }> = [
    { name: "Thử lại (Retry)", value: "retry" },
  ];
  if (args.allowSkip) {
    choices.push({ name: "Bỏ qua bước này và tiếp tục (Skip)", value: "skip" });
  }
  choices.push({ name: "Tạm ngưng init — chạy lại sau (Abort)", value: "abort" });

  // Non-interactive: `--on-failure` thay menu, mặc định `abort`. Tự retry/skip khi
  // không ai nhìn là cách sinh trạng thái nửa vời trên đĩa.
  if (isNonInteractive()) {
    const action = getActiveFlag("onFailure") ?? "abort";
    if (action === "abort") {
      throw new MissingFlagError({
        missingFlag: "--on-failure",
        command: "add repo",
        hint: "--on-failure retry|skip (mặc định abort)",
      });
    }
    return action as RecoveryAction;
  }

  return (await selectOrFlag(undefined, ON_FAILURE_FLAG_SPEC, {
    message: "Cách xử lý?",
    choices,
  })) as RecoveryAction;
}

// Prompt user input manual value khi automated detection fail.
// Vd: workspace name conflict → user tự nhập tên khác.
// Trả về giá trị user nhập, hoặc throw UserAbortedRecoveryError nếu abort.
export async function promptManualValueOrAbort(args: {
  message: string;
  validate?: (v: string) => boolean | string;
  abortMessage?: string;
}): Promise<string> {
  // Allow user nhập empty để abort (Ctrl+C vẫn work).
  // Hoặc nhập value hợp lệ.
  const value = await inputOrFlag(getActiveFlag("remoteUrl"), REMOTE_URL_FLAG_SPEC, {
    message: args.message,
    validate: args.validate ?? ((v) => v.trim().length > 0 || "Giá trị không được rỗng"),
  });
  if (!value.trim()) {
    throw new UserAbortedRecoveryError(
      args.abortMessage ?? "User để rỗng giá trị — abort recovery.",
    );
  }
  return value.trim();
}
